import { Box, Card, Checkbox, Grid, Group, Image, Paper, Skeleton, Stack, Text, Title, Tooltip } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { ActionsRender } from '../components/actions_render';
import InputField, { InputFieldTypes } from '../components/input_field';
import Table from '../components/table';
import { useApp } from '../contexts/app.context';
import { useAuth } from '../contexts/auth.context';
import { DELETE_FILES, GET_ALL_FILES } from '../services/files';
import { formatFileSize } from '../utility/util';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import LazyLoad from "react-lazy-load";
import InfiniteScroll from 'react-infinite-scroll-component';

export default function OverviewPage() {
    const { user: currentUser, role } = useAuth();
    const { confirmDialog } = useApp();

    const [slice, setSlice] = useState(100);
    const [items, setItems] = useState([]);
    const [selectedItems, setSelectedItems] = useState([]);
    const [filters, setFilters] = useState<any>({});
    const [loading, setLoading] = React.useState(false);

    const { period } = filters ?? {}
    const [start, end] = period ?? []

    const startTs = start ? new Date(start).getTime() : null
    const endTs = end ? new Date(end).getTime() : null

    const {
        filteredItems,
        totalSize,
        selectedSize,
        allItemsSelected,
        allItemsIds,
    } = useMemo(() => {
        let total = 0
        let selected = 0
        const filtered = []
        const allIds = []

        const selectedSet = new Set(selectedItems)

        for (const it of items) {
            const size = isNaN(it.size) ? 0 : it.size
            total += size
            const ts = new Date(it.createdAt).getTime()
            if ((startTs && ts < startTs) || (endTs && ts > endTs)) continue
            filtered.push(it)
            allIds.push(it._id)
            if (selectedSet.has(it._id)) selected += size
        }

        const allSelected = selectedSet.size > 0 && filtered.every(it => selectedSet.has(it._id))

        return { filteredItems: filtered, totalSize: total, selectedSize: selected, allItemsSelected: allSelected, allItemsIds: allIds }
    }, [items, startTs, endTs, selectedItems])

    const shownItems = useMemo(() => (
        filteredItems.slice(0, slice)
    ), [filteredItems, slice])

    const loadFiles = () => {
        setLoading(true);

        GET_ALL_FILES()
            .then(res => {
                setItems(res);
                setLoading(false);
            })
            .catch(err => {
                setLoading(false);
                showNotification({ message: err.message, color: 'red' });
            })
    }

    useEffect(loadFiles, [])
    useEffect(() => { setSelectedItems([]); }, [filters])

    return <>
        <Paper p="md" shadow='xs'>
            <Stack>
                <Group style={{ justifyContent: 'space-between' }} align="flex-end">
                    <Group style={{ flex: 1 }} align="flex-end">

                        <ActionsRender
                            disabled={selectedItems.length === 0}
                            actions={[
                                {
                                    title: "Deletar Arquivos",
                                    onClick: () => {
                                        const previous = items;
                                        const previousSelected = selectedItems;
                                        setItems(its => its.filter(i => !selectedItems.includes(i._id)));
                                        setSelectedItems([]);
                                        DELETE_FILES(selectedItems)
                                            .then(() => { })
                                            .catch((err: any) => {
                                                setItems(previous);
                                                setSelectedItems(previousSelected);
                                                showNotification({ message: err.message })
                                            })
                                    }
                                }
                            ]}
                        />
                        <Tooltip label="Selecionar Todos">
                            <Checkbox
                                checked={allItemsSelected}
                                onChange={() => setSelectedItems(si => (
                                    allItemsSelected
                                        ? []
                                        : allItemsIds
                                ))}
                            />
                        </Tooltip>
                        <div style={{ minWidth: 200 }}>
                            <InputField
                                title="Período"
                                fieldType={InputFieldTypes.DATERANGE}
                                value={filters.period}
                                name="period"
                                onChange={({ period }) => setFilters(f => ({ ...f, period }))}
                            />
                        </div>
                    </Group>
                    <Title order={4} color="dark" align='right'>Selecionado: {formatFileSize(selectedSize)}/{formatFileSize(totalSize)}</Title>
                </Group>
                <InfiniteScroll
                    dataLength={shownItems.length}
                    next={() => {
                        setSlice(s => s + 20);
                    }}
                    style={{ overflow: 'hidden' }}
                    hasMore={items.length > shownItems.length || loading}
                    loader={
                        <Skeleton mt="md" animate variant="rectangular" height={100} />
                    }
                    endMessage={<p style={{ textAlign: 'center' }}></p>}
                >
                    <Grid>
                        {shownItems.map(it => <Grid.Col md={3} p="xs">
                            <Tooltip label={`${it.filename} - ${moment(it.createdAt).format('DD/MM/YYYY')} (${formatFileSize(it.size)})`}>
                                <Card
                                    key={it._id}
                                    shadow="sm"
                                    padding="xs"
                                    radius="md"
                                    withBorder
                                    h={140}
                                    onDoubleClick={() => window.open(it.url, "_blank")}
                                    style={{
                                        cursor: "pointer",
                                        border: selectedItems.includes(it._id) ? "2px solid #1c7ed6" : "1px solid #dee2e6",
                                        position: "relative",
                                        overflow: "hidden",
                                    }}
                                    onClick={(e) => {
                                        const lastSelectedIndex = items.findIndex(f => selectedItems.slice(-1)[0] === f._id);
                                        const currentIndex = items.findIndex((f) => f._id === it._id);

                                        if (e.shiftKey && lastSelectedIndex !== null) {
                                            const start = Math.min(lastSelectedIndex, currentIndex);
                                            const end = Math.max(lastSelectedIndex, currentIndex);

                                            const rangeIds = items.slice(start, end + 1).map((f) => f._id);

                                            setSelectedItems((prev) => Array.from(new Set([...prev, ...rangeIds])));
                                        } else {
                                            setSelectedItems((prev) =>
                                                prev.includes(it._id)
                                                    ? prev.filter((id) => id !== it._id)
                                                    : [...prev, it._id]
                                            );
                                        }
                                    }}
                                >
                                    {["mp4", "mov"].includes((it.url ?? "").toLowerCase().split(".").slice(-1)[0])
                                        ? <LazyLoad height="100%" offset={200}>
                                            <video
                                                src={`${it.url}#t=0.5`}
                                                style={{
                                                    width: "100%",
                                                    height: "100%",
                                                    objectFit: "cover",
                                                }}
                                                muted
                                                preload="metadata"
                                                playsInline={false}
                                            />
                                        </LazyLoad>
                                        : <LazyLoadImage
                                            alt={it.filename}
                                            height={120}
                                            src={it.url}
                                            width="100%"
                                            scrollPosition={0}
                                            blur
                                            efect="blur"
                                            style={{ objectFit: 'contain' }}
                                        />}
                                </Card>
                            </Tooltip>
                        </Grid.Col>)}
                    </Grid>
                </InfiniteScroll>
                {/* <Table
                    loading={loading}
                    data={shownItems}
                    columns={[
                        {
                            title: () => <Checkbox
                                checked={selectedItems.length > 0 && shownItems.every(it => selectedItems.includes(it._id))}
                                onChange={() => setSelectedItems(si => (
                                    shownItems.every(it => selectedItems.includes(it._id))
                                        ? []
                                        : shownItems.map(i => i._id)
                                ))}
                            />,
                            key: "select",
                            render: item => <Checkbox
                                checked={selectedItems.includes(item._id)}
                                onChange={() => setSelectedItems(si => (
                                    selectedItems.includes(item._id)
                                        ? si.filter(it => it !== item._id)
                                        : [...si, item._id]
                                ))}
                            />
                        },
                        { title: "Data", key: "createdAt", render: item => moment(item.createdAt).format("DD/MM/YYYY") },
                        { title: "Tamanho", key: "size", render: item => formatFileSize(item.size) },
                        { title: "Arquivo", key: "original_name", render: item => item.original_name ?? item.filename },
                    ]}
                    actions={[
                        {
                            title: "Visualizar",
                            onClick: ({ item }) => window.open((item.url ?? item.generatedUrl), "_blank")
                        },
                        {
                            title: "Deletar", color: "red", onClick: ({ item }) => {
                                confirmDialog(
                                    { text: "Realmente deseja deletar esse arquivo?" },
                                    (({ confirmed }) => {
                                        if (confirmed) {
                                            const previous = items;
                                            setItems(its => its.filter(i => i._id !== item._id))
                                            DELETE_FILES([item._id])
                                                .then(() => { })
                                                .catch((err: any) => {
                                                    setItems(previous);
                                                    showNotification({ message: err.message })
                                                })
                                        }
                                    })
                                )
                            }
                        },
                    ]}
                /> */}
            </Stack>
        </Paper>
    </>
}
